import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CaretUp,
  CaretDown,
  DotsThreeVertical,
  ArrowClockwise,
  EnvelopeSimple,
  WhatsappLogo,
  FilePdf,
  FileXls,
  Copy,
  XCircle,
  Eye,
  PaperPlaneTilt,
} from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import type {
  InvoiceListSnapshot,
  InvoiceLabels,
  Locale,
  InvoiceRow,
  InvoiceSortField,
  InvoiceListFilters,
} from "./types";
import { fmtMoney, fmtDate, fmtTaxNumber, isOverdue, relativeOverdueDays } from "./utils";
import { InvoiceStatusChip } from "./InvoiceStatusChip";

interface Props {
  snapshot: InvoiceListSnapshot;
  labels: InvoiceLabels;
  locale: Locale;
  selectedIds: readonly string[];
  filteredRows: readonly InvoiceRow[];
  onSelectionChange: (ids: readonly string[]) => void;
  onSort: (field: InvoiceSortField) => void;
  sort: InvoiceListFilters["sort"];
  onRowClick: (id: string) => void;
  onIssue?: (id: string) => void;
  onSend?: (row: InvoiceRow, channel: "email" | "whatsapp") => void;
  onVoid?: (id: string) => void;
  onDownload?: (id: string, format: "pdf" | "ubl_xml") => void;
  onCopyPayLink?: (id: string) => void;
}

export function InvoiceDataGrid({
  snapshot: _snapshot,
  labels,
  locale,
  selectedIds,
  filteredRows,
  onSelectionChange,
  onSort,
  sort,
  onRowClick,
  onIssue,
  onSend,
  onVoid,
  onDownload,
  onCopyPayLink,
}: Props) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [focusedIdx, setFocusedIdx] = React.useState(0);
  const today = new Date();

  const allChecked =
    filteredRows.length > 0 && filteredRows.every((r) => selectedIds.includes(r.id));
  const someChecked = filteredRows.some((r) => selectedIds.includes(r.id));

  const toggleAll = () => {
    if (allChecked) {
      onSelectionChange(selectedIds.filter((id) => !filteredRows.some((r) => r.id === id)));
    } else {
      const set = new Set(selectedIds);
      filteredRows.forEach((r) => set.add(r.id));
      onSelectionChange(Array.from(set));
    }
  };

  const toggleOne = (id: string) => {
    if (selectedIds.includes(id)) onSelectionChange(selectedIds.filter((x) => x !== id));
    else onSelectionChange([...selectedIds, id]);
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIdx((i) => Math.min(filteredRows.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      const row = filteredRows[focusedIdx];
      if (row) onRowClick(row.id);
    }
  };

  const sortableHeader = (field: InvoiceSortField, label: string, align: "left" | "right" = "left") => {
    const active = sort.field === field;
    return (
      <button
        type="button"
        onClick={() => onSort(field)}
        className={cn(
          "inline-flex items-center gap-1 text-xs font-semibold text-biz-foreground-muted uppercase tracking-wider hover:text-biz-foreground biz-focus-ring",
          align === "right" && "justify-end w-full",
          active && "text-biz-foreground",
        )}
      >
        <span>{label}</span>
        {active &&
          (sort.dir === "asc" ? (
            <CaretUp size={10} weight="bold" />
          ) : (
            <CaretDown size={10} weight="bold" />
          ))}
      </button>
    );
  };

  return (
    <div
      ref={containerRef}
      onKeyDown={handleKey}
      tabIndex={0}
      role="grid"
      aria-label={labels.page.title}
      aria-rowcount={filteredRows.length + 1}
      className="bg-biz-surface-elevated border border-biz-border rounded-[10px] biz-shadow-sm overflow-hidden biz-focus-ring"
    >
      <div className="overflow-x-auto biz-scrollbar">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-biz-surface border-b border-biz-border">
            <tr className="h-10">
              <th scope="col" className="w-10 px-3 text-center">
                <input
                  type="checkbox"
                  checked={allChecked}
                  ref={(el) => {
                    if (el) el.indeterminate = !allChecked && someChecked;
                  }}
                  onChange={toggleAll}
                  aria-label={labels.row.selectAll}
                  className="h-4 w-4 accent-biz-primary"
                />
              </th>
              <th scope="col" className="px-3 text-left">
                {sortableHeader("invoiceNumber", labels.columns.invoiceNumber)}
              </th>
              <th scope="col" className="px-3 text-left">
                {sortableHeader("issuedAt", labels.columns.issuedAt)}
              </th>
              <th scope="col" className="px-3 text-left">
                {sortableHeader("customerName", labels.columns.customer)}
              </th>
              <th scope="col" className="px-3 text-left w-20">
                <span className="text-xs font-semibold text-biz-foreground-muted uppercase tracking-wider">
                  {labels.columns.type}
                </span>
              </th>
              <th scope="col" className="px-3 text-right w-32">
                {sortableHeader("totalMinor", labels.columns.total, "right")}
              </th>
              <th scope="col" className="px-3 text-right w-32">
                <span className="text-xs font-semibold text-biz-foreground-muted uppercase tracking-wider">
                  {labels.columns.balance}
                </span>
              </th>
              <th scope="col" className="px-3 text-left w-28">
                <span className="text-xs font-semibold text-biz-foreground-muted uppercase tracking-wider">
                  {labels.columns.status}
                </span>
              </th>
              <th scope="col" className="px-3 text-left w-24">
                <span className="text-xs font-semibold text-biz-foreground-muted uppercase tracking-wider">
                  {labels.columns.delivery}
                </span>
              </th>
              <th scope="col" className="w-12" aria-label={labels.columns.actions} />
            </tr>
          </thead>
          <tbody>
            <AnimatePresence initial={false}>
              {filteredRows.map((row, idx) => {
                const overdue = isOverdue(row, today);
                const isSelected = selectedIds.includes(row.id);
                const isDraft = row.status === "draft";
                const isFailed = row.status === "failed";
                const isVoided = row.status === "voided";

                return (
                  <motion.tr
                    key={row.id}
                    layout
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    onClick={(e) => {
                      const target = e.target as HTMLElement;
                      if (target.closest("button, input, [role='menu']")) return;
                      onRowClick(row.id);
                    }}
                    onFocus={() => setFocusedIdx(idx)}
                    role="row"
                    aria-selected={isSelected}
                    className={cn(
                      "h-14 border-b border-biz-border transition-colors cursor-pointer",
                      "hover:bg-biz-muted/40",
                      isSelected && "bg-biz-primary-subtle/40",
                      focusedIdx === idx && "bg-biz-muted/60",
                      isVoided && "opacity-60",
                      // Left border accent
                      "relative",
                    )}
                  >
                    {/* Accent left border via pseudo */}
                    <td
                      className={cn(
                        "w-10 px-3 text-center relative",
                        isFailed && "before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 before:bg-biz-danger",
                        overdue && !isFailed && "before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 before:bg-biz-warning",
                        isDraft && "before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 before:border-l-2 before:border-dashed before:border-biz-border-strong",
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleOne(row.id)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={labels.row.selectRow}
                        className="h-4 w-4 accent-biz-primary"
                      />
                    </td>
                    <td className="px-3 align-middle">
                      <div className="flex items-center gap-2">
                        {isDraft && (
                          <span className="font-mono-num text-[10px] uppercase tracking-wider px-1.5 h-5 inline-flex items-center rounded border border-dashed border-biz-border-strong text-biz-foreground-muted">
                            {labels.row.draftBadge}
                          </span>
                        )}
                        <span className="font-mono-num text-[13px] text-biz-foreground">
                          {row.invoiceNumber ?? "—"}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 align-middle text-biz-foreground-muted text-[13px]">
                      {fmtDate(row.issuedAt, locale)}
                      {overdue && (
                        <div className="text-[10px] font-semibold text-biz-warning mt-0.5">
                          {labels.row.overdue} · {relativeOverdueDays(row.dueAt, today)}d
                        </div>
                      )}
                    </td>
                    <td className="px-3 align-middle">
                      <div className="font-medium text-biz-foreground truncate max-w-[260px]">
                        {row.customer.name}
                      </div>
                      {row.customer.taxNumber && (
                        <div className="font-mono-num text-[11px] text-biz-foreground-subtle">
                          {fmtTaxNumber(row.customer.taxNumber)}
                        </div>
                      )}
                    </td>
                    <td className="px-3 align-middle">
                      <span
                        className={cn(
                          "inline-flex items-center px-2 h-6 rounded-full text-[11px] font-semibold",
                          row.type === "e_fatura"
                            ? "bg-biz-accent-subtle text-biz-accent-foreground"
                            : "bg-biz-info-subtle text-biz-info",
                        )}
                      >
                        {labels.type[row.type]}
                      </span>
                    </td>
                    <td className="px-3 align-middle text-right">
                      <div className="font-mono-num text-[13px] font-semibold text-biz-foreground">
                        {fmtMoney(row.totalMinor, locale)}
                      </div>
                    </td>
                    <td className="px-3 align-middle text-right">
                      <div
                        className={cn(
                          "font-mono-num text-[13px]",
                          row.balanceMinor === 0 ? "text-biz-success" : "text-biz-warning font-semibold",
                        )}
                      >
                        {fmtMoney(row.balanceMinor, locale)}
                      </div>
                    </td>
                    <td className="px-3 align-middle">
                      <InvoiceStatusChip status={row.status} labels={labels} size="sm" />
                    </td>
                    <td className="px-3 align-middle">
                      <DeliveryPill row={row} labels={labels} />
                    </td>
                    <td className="w-12 px-2 align-middle text-center">
                      <RowActions
                        row={row}
                        labels={labels}
                        onIssue={onIssue}
                        onSend={onSend}
                        onVoid={onVoid}
                        onDownload={onDownload}
                        onCopyPayLink={onCopyPayLink}
                        onView={() => onRowClick(row.id)}
                      />
                    </td>
                  </motion.tr>
                );
              })}
            </AnimatePresence>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DeliveryPill({ row, labels }: { row: InvoiceRow; labels: InvoiceLabels }) {
  const ds = row.deliveryStatus;
  if (ds === "not_sent") {
    return <span className="text-[11px] text-biz-foreground-subtle">{labels.delivery.not_sent}</span>;
  }
  const Icon = ds === "sent_whatsapp" ? WhatsappLogo : EnvelopeSimple;
  return (
    <span className="inline-flex items-center gap-1 text-[11px] text-biz-foreground-muted">
      <Icon size={12} weight="regular" />
      {labels.delivery[ds]}
    </span>
  );
}

function RowActions({
  row,
  labels,
  onIssue,
  onSend,
  onVoid,
  onDownload,
  onCopyPayLink,
  onView,
}: {
  row: InvoiceRow;
  labels: InvoiceLabels;
  onIssue?: (id: string) => void;
  onSend?: (row: InvoiceRow, channel: "email" | "whatsapp") => void;
  onVoid?: (id: string) => void;
  onDownload?: (id: string, format: "pdf" | "ubl_xml") => void;
  onCopyPayLink?: (id: string) => void;
  onView: () => void;
}) {
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  const canVoid = row.status !== "paid" && row.status !== "voided";
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={labels.row.rowActions}
          onClick={stop}
          className="h-9 w-9 inline-flex items-center justify-center rounded-biz-btn text-biz-foreground-muted hover:text-biz-foreground hover:bg-biz-muted biz-focus-ring"
        >
          <DotsThreeVertical size={18} weight="bold" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 bg-biz-surface-elevated border-biz-border">
        <DropdownMenuItem onClick={onView} className="gap-2">
          <Eye size={14} /> {labels.row.viewDetail}
        </DropdownMenuItem>
        {row.status === "draft" && onIssue && (
          <DropdownMenuItem onClick={() => onIssue(row.id)} className="gap-2">
            <PaperPlaneTilt size={14} /> {labels.row.issue}
          </DropdownMenuItem>
        )}
        {row.status === "failed" && onIssue && (
          <DropdownMenuItem onClick={() => onIssue(row.id)} className="gap-2 text-biz-danger">
            <ArrowClockwise size={14} /> {labels.row.failedRetry}
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        {row.pdfUrl && (
          <DropdownMenuItem onClick={() => onDownload?.(row.id, "pdf")} className="gap-2">
            <FilePdf size={14} /> {labels.row.pdf}
          </DropdownMenuItem>
        )}
        {row.type === "e_fatura" && (
          <DropdownMenuItem onClick={() => onDownload?.(row.id, "ubl_xml")} className="gap-2">
            <FileXls size={14} /> {labels.row.xml}
          </DropdownMenuItem>
        )}
        {row.shareUrl && (
          <DropdownMenuItem onClick={() => onCopyPayLink?.(row.id)} className="gap-2">
            <Copy size={14} /> {labels.row.copyShareLink}
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        {row.customer.email && (
          <DropdownMenuItem onClick={() => onSend?.(row, "email")} className="gap-2">
            <EnvelopeSimple size={14} /> {labels.row.sendEmail}
          </DropdownMenuItem>
        )}
        {row.customer.phone && (
          <DropdownMenuItem onClick={() => onSend?.(row, "whatsapp")} className="gap-2">
            <WhatsappLogo size={14} /> {labels.row.sendWhatsapp}
          </DropdownMenuItem>
        )}
        {canVoid && onVoid && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onVoid(row.id)} className="gap-2 text-biz-danger">
              <XCircle size={14} /> {labels.row.void}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
